import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CreateTask from "../CreateTask/CreateTask";
// import { getTasks } from "../../services/Service";
import { useGetTasksQuery, useGetProjectsQuery, useUpdateTaskMutation, useDeleteTaskMutation, useDeleteProjectMutation, useInsertProjectMemberMutation, useLazySearchOneRecordQuery, useGetProjectMembersQuery, useGetOrganizationMembersQuery } from "../../services/rtkClient";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import "./TaskTools.css";
import {
  Settings,
  Check,
  Filter,
  List,
  LayoutGrid,
  Plus,
  Folder,
  ClipboardList as Tasks,
  Menu,
  Download,
  Upload,
  Link,
  Search,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  Pencil as Edit,
  Trash2 as Trash,
  BookText as Book,
  User,
  History
} from "lucide-react";

const TaskTools = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const projectName = state?.projectName || sessionStorage.getItem('projectName') || "";
  const [activeTab, setActiveTab] = useState(state?.activeTab || "Board");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [defaultColumn, setDefaultColumn] = useState("todo");
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [myProjectsOpen, setMyProjectsOpen] = useState(false);
  const [contributionProjectsOpen, setContributionProjectsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState(projectName || "");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [isInviting, setIsInviting] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Auto-open create task modal if navigating from Onboarding
  useEffect(() => {
    if (state?.openCreateTask) {
      setIsCreateModalOpen(true);
      // Clean up the history state so a page refresh doesn't reopen the modal
      const newState = { ...state };
      delete newState.openCreateTask;
      window.history.replaceState({ ...window.history.state, usr: newState }, '');
    }
  }, [state]);

  const { data: serverProjectsData } = useGetProjectsQuery();
  const [updateTaskMutation] = useUpdateTaskMutation();
  const [deleteTaskMutation] = useDeleteTaskMutation();
  const [deleteProjectMutation] = useDeleteProjectMutation();
  const [insertProjectMember] = useInsertProjectMemberMutation();
  const [searchOneRecord] = useLazySearchOneRecordQuery();
  // Cache: userId (string) -> { email, name } — populated from org members + on invite success
  const [memberInfoCache, setMemberInfoCache] = useState({});
  // Fetch org members to build the lookup (organizationId = 1, static)
  const { data: orgMembersRaw } = useGetOrganizationMembersQuery(1);

  const handleDeleteProject = async (e, proj) => {
    e.stopPropagation();
    const projName = proj.name || proj.projectName || "this project";
    if (!window.confirm(`Delete project "${projName}"? This will also remove all associated tasks.`)) return;

    const projId = proj.id || proj.projectId;
    try {
      await deleteProjectMutation(projId).unwrap();
      // If the deleted project was the active one, reset selection
      if (currentProjectId && Number(projId) === Number(currentProjectId)) {
        setSelectedProjectId(null);
        sessionStorage.removeItem('projectId');
        sessionStorage.removeItem('projectName');
        setCurrentProjectName('');
      }
    } catch (err) {
      console.error("Delete Project Error:", err);
      alert("Failed to delete project. Please try again.");
    }
  };

  const handleDeleteAllProjects = async () => {
    if (myProjects.length === 0) {
      alert("No projects to delete.");
      return;
    }
    if (!window.confirm(`Delete ALL ${myProjects.length} project(s)? This action cannot be undone.`)) return;

    let deletedCount = 0;
    for (const proj of myProjects) {
      const projId = proj.id || proj.projectId;
      try {
        await deleteProjectMutation(projId).unwrap();
        deletedCount++;
      } catch (err) {
        console.error(`Failed to delete project ${proj.name}:`, err);
      }
    }

    // Reset active project selection
    setSelectedProjectId(null);
    sessionStorage.removeItem('projectId');
    sessionStorage.removeItem('projectName');
    setCurrentProjectName('');

    alert(`Successfully deleted ${deletedCount} of ${myProjects.length} project(s).`);
  };

  const handleDeleteAllContributionProjects = async () => {
    if (contributionProjects.length === 0) {
      alert("No contribution projects to delete.");
      return;
    }
    if (!window.confirm(`Delete ALL ${contributionProjects.length} contribution project(s)? This action cannot be undone.`)) return;

    let deletedCount = 0;
    for (const proj of contributionProjects) {
      const projId = proj.id || proj.projectId;
      try {
        await deleteProjectMutation(projId).unwrap();
        deletedCount++;
      } catch (err) {
        console.error(`Failed to delete project ${proj.name}:`, err);
      }
    }

    setSelectedProjectId(null);
    sessionStorage.removeItem('projectId');
    sessionStorage.removeItem('projectName');
    setCurrentProjectName('');

    alert(`Successfully deleted ${deletedCount} of ${contributionProjects.length} contribution project(s).`);
  };

  const { myProjects, contributionProjects, recentProjects } = useMemo(() => {
    const serverProjects = Array.isArray(serverProjectsData) ? serverProjectsData : [];

    let currentUserId = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = JSON.parse(userStr);
        currentUserId = userObj.id || userObj._id || userObj.userId;
      }
    } catch (e) {
      console.error("Error parsing user for categorization:", e);
    }

    const my = [];
    const contr = [];

    serverProjects.forEach(proj => {
      const ownerId = proj.userId || proj.ownerId || proj.user_id;
      // Using String comparison for robust ID matching
      if (currentUserId && String(ownerId) === String(currentUserId)) {
        my.push(proj);
      } else {
        contr.push(proj);
      }
    });

    return {
      myProjects: my,
      contributionProjects: contr,
      recentProjects: serverProjects
    };
  }, [serverProjectsData]);

  const currentProjectId = useMemo(() => {
    // Priority: explicit sidebar click > navigation state > sessionStorage > first project from API
    if (selectedProjectId) return selectedProjectId;
    if (state?.projectId) {
      const id = Number(state.projectId);
      sessionStorage.setItem('projectId', String(id));
      return id;
    }
    const stored = sessionStorage.getItem('projectId');
    if (stored) return Number(stored);

    if (recentProjects.length > 0) {
      const id = Number(recentProjects[0].id || recentProjects[0].projectId);
      if (id) {
        sessionStorage.setItem('projectId', String(id));
        const name = recentProjects[0].name || recentProjects[0].projectName;
        if (name) sessionStorage.setItem('projectName', name);
      }
      return id;
    }
    return null;
  }, [recentProjects, state?.projectId, selectedProjectId]);

  const { data: serverTasksData, refetch: refetchTasks } = useGetTasksQuery(currentProjectId, { skip: !currentProjectId, refetchOnFocus: true, refetchOnMountOrArgChange: true });

  // Members list – must be after currentProjectId is defined
  const { data: projectMembersData = [], refetch: refetchMembers } = useGetProjectMembersQuery(
    currentProjectId,
    { skip: !currentProjectId }
  );

  // Build userId -> { email, name } lookup from org members whenever they load
  React.useEffect(() => {
    if (!orgMembersRaw) return;
    const arr = Array.isArray(orgMembersRaw)
      ? orgMembersRaw
      : orgMembersRaw?.data || orgMembersRaw?.schema || [];
    if (!Array.isArray(arr) || arr.length === 0) return;
    const map = {};
    arr.forEach(m => {
      const raw = m.schema || m;
      const uid = String(raw.userId || raw.user_id || raw.id || "");
      if (!uid) return;
      const email = raw.email || raw.userEmail || "";
      const name = raw.name || raw.userName || raw.fullName || raw.firstName || email || "";
      if (uid) map[uid] = { email, name };
    });
    if (Object.keys(map).length > 0) {
      setMemberInfoCache(prev => ({ ...map, ...prev })); // prev wins (invite cache takes priority)
    }
  }, [orgMembersRaw]);


  useEffect(() => {
    if (!state?.projectName && !sessionStorage.getItem('projectName') && recentProjects.length > 0) {
      const firstProjectName = recentProjects[0].name || recentProjects[0].projectName;
      if (firstProjectName) {
        setCurrentProjectName(firstProjectName);
        sessionStorage.setItem('projectName', firstProjectName);
      }
    }
    // Update currentProjectName when navigation state changes
    if (state?.projectName) {
      setCurrentProjectName(state.projectName);
      sessionStorage.setItem('projectName', state.projectName);
    }
    // Persist projectId to sessionStorage whenever we get real projects
    if (recentProjects.length > 0 && !sessionStorage.getItem('projectId')) {
      const id = recentProjects[0].id || recentProjects[0].projectId;
      if (id) sessionStorage.setItem('projectId', String(id));
    }
  }, [recentProjects, state?.projectName, state?.projectId]);



  const tasks = useMemo(() => {
    const serverTasksRaw = Array.isArray(serverTasksData) ? serverTasksData : [];
    const grouped = { todo: [], inprogress: [], review: [], done: [] };

    serverTasksRaw.forEach(t => {
      // Filter by project ID (redundant but safe if API returns all)
      const tProjectId = Number(t.projectId || t.project_id);
      if (currentProjectId && tProjectId !== currentProjectId) return;

      const col = (t.status || t.column || "todo").toLowerCase().replace(/\s/g, "");
      const normalized = {
        ...t,
        id: t.id || t.issueId || `KAN-${t.id || t.issueId}`,
        title: t.summary || t.task || t.title || "Untitled",
        task: t.summary || t.task || t.title || "Untitled",
        date: t.dueDate || t.date || (t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "Feb 10, 2026"),
      };

      if (grouped[col]) grouped[col].push(normalized);
      else grouped.todo.push(normalized);
    });

    return grouped;
  }, [serverTasksData, currentProjectId]);

  const [optimisticTasks, setOptimisticTasks] = useState(null);

  useEffect(() => {
    setOptimisticTasks(null);
  }, [tasks]);

  const displayTasks = optimisticTasks || tasks;

  const [searchQuery, setSearchQuery] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [showTimeSpentPopup, setShowTimeSpentPopup] = useState(false);
  const [pendingTaskMovement, setPendingTaskMovement] = useState(null);
  const [timeSpentInput, setTimeSpentInput] = useState("");
  const fileInputRef = useRef(null);

  const metrics = React.useMemo(() => {
    const totalProjects = recentProjects.length || 0;
    const tasksInProgress = (displayTasks.inprogress || []).length;
    const completedTasks = (displayTasks.done || []).length;
    const membersSet = new Set();
    Object.values(displayTasks).forEach((arr) => {
      (arr || []).forEach((t) => {
        if (t.assignee && t.assignee !== "Unassigned")
          membersSet.add(t.assignee);
      });
    });
    const teamMembers = Math.max(1, membersSet.size);
    return { totalProjects, tasksInProgress, completedTasks, teamMembers };
  }, [displayTasks, recentProjects]);

  // Local storage synchronization removed per user request

  useEffect(() => {
    const fetchTasksFromAPI = async () => {
      try {
        // const data = await getTasks();
        // if (data && typeof data === "object" && !Array.isArray(data)) {
        //   setTasks(data);
        // } else if (Array.isArray(data)) {
        //   const grouped = { todo: [], inprogress: [], review: [], done: [] };
        //   data.forEach((task) => {
        //     const col = (task.status || task.column || "todo")
        //       .toLowerCase()
        //       .replace(" ", "");
        //     if (grouped[col]) grouped[col].push(task);
        //     else grouped.todo.push(task);
        //   });
        //   setTasks(grouped);
        // }
      } catch (err) {
        console.error("Error fetching tasks:", err);
      }
    };
    fetchTasksFromAPI();
  }, []);


  const handleSubItemClick = (type, value) => {
    if (type === "Project") {
      setCurrentProjectName(value);
      sessionStorage.setItem('projectName', value);

      // Find the project and update the reactive selectedProjectId state
      // so currentProjectId memo recalculates and useGetTasksQuery refetches
      const project = recentProjects.find(p => (p.name || p.projectName) === value);
      if (project) {
        const projId = Number(project.id || project.projectId);
        if (projId) {
          sessionStorage.setItem('projectId', String(projId));
          setSelectedProjectId(projId);
        }
      }
    } else {
      alert(`Filtering by ${type}: ${value}`);
    }
  };

  const handleTaskClick = (taskTitle) =>
    console.log(`Clicked Task: ${taskTitle}`);
  const handleButtonClick = (btnName) =>
    console.log(`Clicked Button: ${btnName}`);

  const handleCreateTask = (columnLabel) => {
    const columnMap = {
      "To Do": "todo",
      "In Progress": "inprogress",
      "In Review": "review",
      Done: "done",
    };
    setDefaultColumn(columnMap[columnLabel] || "todo");
    setEditingTask(null);
    setIsCreateModalOpen(true);
  };

  const handleAddTaskToBoard = (newTaskData) => {
    console.log("Receiving new task data in TaskTools:", newTaskData);
    const fullData = newTaskData?.data || newTaskData;
    console.log("Extracted fullData:", fullData);

    const taskId = fullData?.issueKey || fullData?.id || `KAN-${Math.floor(Math.random() * 100000)}`;
    const rawColumn = (fullData?.status || fullData?.column || "todo").toLowerCase().replace(/\s+/g, "");
    const column = ["todo", "inprogress", "review", "done"].includes(rawColumn) ? rawColumn : "todo";

    console.log("Adding/Updating task with ID:", taskId, "to column:", column);

    // No manual state update needed anymore, RTK Query handles it through invalidatesTags: ["Task"]
    // and automatic refetch. We just close the modal.
    setIsCreateModalOpen(false);
    refetchTasks();

    setIsCreateModalOpen(false);
  };

  const handleEditTask = (taskId) => {
    let found = null;
    Object.entries(displayTasks).forEach(([col, arr]) => {
      arr.forEach((t) => {
        if (t.id === taskId) found = { ...t, column: col };
      });
    });
    if (found) {
      setDefaultColumn(found.column || "todo");
      setEditingTask({ id: taskId, data: found });
      setIsCreateModalOpen(true);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    // Optimistic UI for delete
    let foundCol = null;
    let foundIdx = -1;
    Object.entries(displayTasks).forEach(([col, arr]) => {
      const idx = arr.findIndex(t => t.id === taskId);
      if (idx !== -1) {
        foundCol = col;
        foundIdx = idx;
      }
    });

    if (foundCol) {
      const newTasks = {
        todo: [...displayTasks.todo],
        inprogress: [...displayTasks.inprogress],
        review: [...displayTasks.review],
        done: [...displayTasks.done]
      };
      newTasks[foundCol].splice(foundIdx, 1);
      setOptimisticTasks(newTasks);
    }

    try {
      // Use the numeric ID if possible, otherwise taskId
      const numericId = parseInt(taskId.toString().replace("KAN-", ""));
      await deleteTaskMutation(isNaN(numericId) ? taskId : numericId).unwrap();

      // No manual tasks update needed, RTK Query refetches automatically
    } catch (err) {
      console.error("Delete Task Error:", err);
      alert("Failed to delete task. Please try again.");
      refetchTasks(); // Refetch to restore if sync failed
    }
  };

  const exportCSV = () => {
    const rows = [["id", "title", "date", "assignee", "column"]];
    Object.entries(displayTasks).forEach(([col, arr]) => {
      arr.forEach((t) =>
        rows.push([t.id, t.title, t.date, t.assignee || "", col]),
      );
    });
    const csv = rows
      .map((r) =>
        r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentProjectName.replace(/\s+/g, "_")}_tasks.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImportFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (!lines.length) return;
      const header = lines
        .shift()
        .split(",")
        .map((h) => h.replace(/"/g, "").trim());
      const parsed = lines.map((line) => {
        const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
        const obj = {};
        header.forEach((h, i) => (obj[h] = cols[i]));
        return obj;
      });
      const next = { todo: [], inprogress: [], review: [], done: [] };
      parsed.forEach((p) => {
        const col = p.column || p.status || "todo";
        next[col] = next[col] || [];
        next[col].push({
          id: p.id || `KAN-${Math.floor(Math.random() * 100000)}`,
          title: p.title || p.task || "Imported",
          date: p.date || "",
          assignee: p.assignee || "",
        });
      });
      // No manual tasks update needed, RTK Query refetches automatically
    };
    reader.readAsText(f);
    e.target.value = null;
  };

  // Filtering helper
  const filterTasks = (arr) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (t) =>
        (t.title || t.summary || t.task || "").toLowerCase().includes(q) ||
        (String(t.id) || "").toLowerCase().includes(q) ||
        (t.assignee || "").toLowerCase().includes(q),
    );
  };

  // ── Drag & Drop state and handlers ──────────────────────────────────────────
  const dragInfo = useRef(null); // { fromCol, taskIndex }

  const handleDragStart = (e, fromCol, taskIndex) => {
    try {
      e.dataTransfer.setData("text/plain", "");
    } catch (err) { }
    e.dataTransfer.effectAllowed = "move";
    dragInfo.current = { fromCol, taskIndex };
  };

  const handleDragEnd = () => {
    dragInfo.current = null;
  };

  const handleDragOver = (e, col, idx = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };


  const handleDrop = async (toCol, toIndex = null) => {
    console.log(`Dropped to column: ${toCol}, index: ${toIndex}`);
    if (!dragInfo.current) return;
    const { fromCol, taskIndex } = dragInfo.current;

    if (
      fromCol === toCol &&
      (toIndex === null || toIndex === taskIndex || toIndex === taskIndex + 1)
    ) {
      dragInfo.current = null;
      return;
    }

    if (toCol === "done" && fromCol !== "done") {
      setPendingTaskMovement({ fromCol, taskIndex, toCol, toIndex });
      setShowTimeSpentPopup(true);
      dragInfo.current = null;
      return;
    }

    const taskToMove = displayTasks[fromCol][taskIndex];

    // Optimistic UI
    const newTasks = {
      todo: [...displayTasks.todo],
      inprogress: [...displayTasks.inprogress],
      review: [...displayTasks.review],
      done: [...displayTasks.done]
    };
    newTasks[fromCol].splice(taskIndex, 1);
    const spliceIndex = toIndex !== null ? toIndex : newTasks[toCol].length;
    newTasks[toCol].splice(spliceIndex, 0, taskToMove);
    setOptimisticTasks(newTasks);

    try {
      // Resolve the numeric issueId — taskToMove.id may be "KAN-5" or just 5
      const rawId = taskToMove.issueId || taskToMove.id;
      const numericId = parseInt(String(rawId).replace(/[^0-9]/g, ""));
      const issueId = !isNaN(numericId) ? numericId : rawId;

      // Find the original raw task from the server data for a full payload
      const originalTask = Array.isArray(serverTasksData)
        ? serverTasksData.find(t => {
          const tid = t.issueId || t.id;
          return String(tid) === String(issueId) || String(tid) === String(rawId);
        })
        : null;

      if (!originalTask) {
        console.warn("Could not find original task for update, aborting sync", { issueId, rawId });
        refetchTasks();
        dragInfo.current = null;
        return;
      }

      // Build a complete payload — spread all original fields, only override status
      const payload = {
        ...originalTask,
        issueId: originalTask.issueId || originalTask.id || issueId,
        status: toCol,
        orderIndex: toIndex !== null ? toIndex : (originalTask.orderIndex || 0),
      };

      console.log("Updating task status via drag:", payload);
      await updateTaskMutation(payload).unwrap();
    } catch (err) {
      console.error("Task move sync failed:", err);
      refetchTasks(); // Refetch to restore correct state if sync failed
    }

    dragInfo.current = null;
  };

  const handleTimeSpentSubmit = async () => {
    if (!pendingTaskMovement) return;
    const { fromCol, taskIndex, toCol, toIndex } = pendingTaskMovement;
    const movedTask = displayTasks[fromCol][taskIndex];

    try {
      // Resolve the numeric issueId
      const rawId = movedTask.issueId || movedTask.id;
      const numericId = parseInt(String(rawId).replace(/[^0-9]/g, ""));
      const issueId = !isNaN(numericId) ? numericId : rawId;

      const originalTask = Array.isArray(serverTasksData)
        ? serverTasksData.find(t => {
          const tid = t.issueId || t.id;
          return String(tid) === String(issueId) || String(tid) === String(rawId);
        })
        : null;

      if (!originalTask) {
        console.warn("Could not find original task for done update", { issueId, rawId });
        refetchTasks();
        setShowTimeSpentPopup(false);
        setPendingTaskMovement(null);
        setTimeSpentInput("");
        return;
      }

      const payload = {
        ...originalTask,
        issueId: originalTask.issueId || originalTask.id || issueId,
        status: toCol,
        timeSpent: Number(timeSpentInput) || originalTask.timeSpent || 0,
        orderIndex: toIndex !== null ? toIndex : (originalTask.orderIndex || 0),
        resolution: "resolved",
        resolvedAt: new Date().toISOString(),
      };

      console.log("Updating task to done via drag:", payload);
      await updateTaskMutation(payload).unwrap();
    } catch (err) {
      console.error("Task move (done) sync failed:", err);
      refetchTasks();
    }

    setShowTimeSpentPopup(false);
    setPendingTaskMovement(null);
    setTimeSpentInput("");
  };

  const getCurrentUser = () => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) return JSON.parse(stored);
    } catch (e) { }
    return null;
  };

  const handleInvitePeople = async () => {
    const email = inviteEmail.trim();
    if (!email) { toast.warning("Please enter an email address."); return; }
    if (!currentProjectId) { toast.warning("No project selected. Please select a project first."); return; }

    setIsInviting(true);
    try {
      const searchResponse = await searchOneRecord(email).unwrap();
      console.log("DEBUG searchOneRecord response:", JSON.stringify(searchResponse));

      const foundEmail =
        searchResponse.schema?.email || searchResponse.email || searchResponse.data?.email;
      const foundUserId =
        searchResponse.schema?.id ||
        searchResponse.schema?.userId ||
        searchResponse.id ||
        searchResponse.userId ||
        searchResponse.data?.id ||
        searchResponse.data?.userId;

      if (!foundEmail) { toast.error("User is not registered."); setIsInviting(false); return; }

      // Guard: if we can't resolve a real userId, refuse to proceed
      if (!foundUserId) {
        console.error("Could not resolve userId from search response:", searchResponse);
        toast.error("Could not retrieve user ID. Please try again.");
        setIsInviting(false);
        return;
      }

      // Check if user is already a member of this project
      const alreadyMember = projectMembersData.some(
        (m) => String(m.userId || m.user_id) === String(foundUserId)
      );
      if (alreadyMember) {
        toast.info(`${foundEmail} is already a member of this project.`);
        setIsInviting(false);
        return;
      }

      const currentUser = getCurrentUser();
      const addedBy = Number(currentUser?.id || currentUser?.userId || currentUser?._id || 1);

      await insertProjectMember({
        projectId: Number(currentProjectId),
        userId: Number(foundUserId),  // no || 1 fallback — validated above
        role: inviteRole,
        permissions: {},
        addedBy: addedBy,
        addedAt: new Date().toISOString(),
      }).unwrap();

      // Cache this user's info so the Members tab shows their name immediately
      setMemberInfoCache(prev => ({
        ...prev,
        [String(Number(foundUserId))]: { email: foundEmail, name: foundEmail },
      }));
      toast.success(`${foundEmail} has been invited as ${inviteRole}!`);
      setInviteEmail("");
      setInviteRole("member");
      setIsInviteModalOpen(false);
      // Switch to Members tab & refresh so the new member appears immediately
      setActiveTab("Members");
      refetchMembers();
    } catch (err) {
      console.error("Invite failed:", err);
      // Check all possible paths the SequelizeUniqueConstraintError name might appear
      const errorName =
        err?.data?.error?.name ||
        err?.data?.data?.error?.name ||
        err?.error?.data?.error?.name ||
        err?.error?.name ||
        "";
      const errMsg = err?.data?.msg || err?.message || "";
      const isUniqueConstraint =
        errorName.toLowerCase().includes("unique") ||
        errMsg.toLowerCase().includes("unique") ||
        errMsg.toLowerCase().includes("already");

      if (isUniqueConstraint) {
        toast.info("This user is already a member of the project.");
      } else if (err?.status === 500 || err?.data?.status === 500) {
        // Server error that is NOT a unique constraint – show the real server message
        const msg = err?.data?.msg || "Server error. Please try again.";
        toast.error(msg);
      } else {
        const msg = err?.data?.msg || "Failed to invite user. They may not be registered.";
        toast.error(msg);
      }
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <div className={`task-tools-wrapper ${compactMode ? "compact" : ""}`}>
      <Navbar
        onCreate={() => {
          setEditingTask(null);
          setIsCreateModalOpen(true);
        }}
      />
      <div className="task-tools-container">
        {isSidebarVisible && (
          <aside className="lp-sidebar">
            <div className="lp-sidebar-nav">
              <div
                className="lp-nav-item active"
                onClick={() => navigate()}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <LayoutGrid size={18} />
                </span>
                Dashboard
              </div>

            </div>

            <div className="lp-sidebar-nav secondary">
              {/* My Projects Dropdown */}
              <div
                className="lp-nav-item"
                onClick={() => setMyProjectsOpen(!myProjectsOpen)}
                style={{ cursor: "pointer", display: 'flex', alignItems: 'center' }}
              >
                <span className="lp-nav-icon">
                  <Folder size={18} />
                </span>
                <span style={{ flex: 1 }}>My Projects</span>
                <span
                  className="lp-section-add"
                  onClick={(e) => { e.stopPropagation(); navigate('/onboarding'); }}
                  style={{ cursor: 'pointer', marginRight: '8px', display: 'flex', alignItems: 'center', opacity: 0.7 }}
                  title="Create New Project"
                >
                  <Plus size={14} />
                </span>
                <span className="lp-nav-arrow">
                  {myProjectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {myProjectsOpen && (
                <div className="lp-sub-items">
                  {myProjects.length > 0 ? (
                    myProjects.map((proj, idx) => (
                      <div
                        key={idx}
                        className="lp-sub-item"
                        onClick={() => handleSubItemClick("Project", proj.name)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{proj.name}</span>
                        <span
                          className="lp-project-delete"
                          onClick={(e) => handleDeleteProject(e, proj)}
                          title={`Delete ${proj.name}`}
                        >
                          <Trash size={14} />
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="lp-sub-item no-data">No projects found</div>
                  )
                  }
                  {myProjects.length > 0 && (
                    <button
                      className="lp-delete-all-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteAllProjects(); }}
                    >
                      <Trash size={13} /> Delete All Projects
                    </button>
                  )}
                </div>
              )}

              {/* Contribution Projects Dropdown */}
              <div
                className="lp-nav-item"
                onClick={() => setContributionProjectsOpen(!contributionProjectsOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Users size={18} />
                </span>
                My Contribution Projects
                <span className="lp-nav-arrow">
                  {contributionProjectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {contributionProjectsOpen && (
                <div className="lp-sub-items">
                  {contributionProjects.length > 0 ? (
                    contributionProjects.map((proj, idx) => (
                      <div
                        key={idx}
                        className="lp-sub-item"
                        onClick={() => handleSubItemClick("Project", proj.name)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{proj.name}</span>
                        <span
                          className="lp-project-delete"
                          onClick={(e) => handleDeleteProject(e, proj)}
                          title={`Delete ${proj.name}`}
                        >
                          <Trash size={14} />
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="lp-sub-item no-data">No projects found</div>
                  )}
                  {contributionProjects.length > 0 && (
                    <button
                      className="lp-delete-all-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteAllContributionProjects(); }}
                    >
                      <Trash size={13} /> Delete All Projects
                    </button>
                  )}
                </div>
              )}


              <div
                className="lp-nav-item"
                onClick={() => setStatusOpen(!statusOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Tasks size={18} />
                </span>
                Status
                <span className="lp-nav-arrow">
                  {statusOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {statusOpen && (
                <div className="lp-sub-items">
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "Completed")}
                  >
                    Completed
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "In Progress")}
                  >
                    In Progress
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "Hold")}
                  >
                    Hold
                  </div>
                </div>
              )}
            </div>

            <div className="lp-sidebar-footer">
              <button
                className="lp-invite-btn"
                onClick={() => setIsInviteModalOpen(true)}
              >
                <Users size={16} /> Invite People
              </button>
            </div>
          </aside>
        )}

        <main className="tt-main">
          <div className="tt-board-header">
            <div className="tt-header-top">
              <button
                className="sidebar-toggle-btn"
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                title={isSidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
              >
                <Menu size={18} />
              </button>
              <button
                className="btn-secondary"
                onClick={() => setCompactMode((s) => !s)}
                title={compactMode ? "Disable compact" : "Enable compact"}
                style={{ marginLeft: 8 }}
              >
                {compactMode ? "Compact: On" : "Compact"}
              </button>

            </div>

            <div className="tt-project-title">
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  background: "linear-gradient(135deg, #0052cc, #6554c0)",
                  borderRadius: "4px",
                }}
              ></div>
              <h1>{currentProjectName}</h1>
              <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                <button
                  className="btn-icon"
                  onClick={() => handleButtonClick("Share")}
                >
                  <Link size={18} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleButtonClick("Board Settings")}
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>

            <div className="tt-tabs">
              <div
                className={`tt-tab ${activeTab === "Summary" ? "active" : ""}`}
                onClick={() =>
                  navigate("/summary", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Summary
              </div>
              <div
                className={`tt-tab ${activeTab === "Board" ? "active" : ""}`}
                onClick={() => setActiveTab("Board")}
              >
                Board
              </div>
              <div
                className={`tt-tab ${activeTab === "List" ? "active" : ""}`}
                onClick={() =>
                  navigate("/listpage", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Tasks List
              </div>
              <div
                className={`tt-tab ${activeTab === "Timeline" ? "active" : ""}`}
                onClick={() =>
                  navigate("/timeline", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Timeline
              </div>
              <div
                className={`tt-tab ${activeTab === "Calendar" ? "active" : ""}`}
                onClick={() =>
                  navigate("/calendar", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Calendar
              </div>
              <div
                className={`tt-tab ${activeTab === "Members" ? "active" : ""}`}
                onClick={() => { setActiveTab("Members"); refetchMembers(); }}
              >
                <Users size={14} style={{ marginRight: 5, verticalAlign: "middle" }} />
                Members
              </div>
            </div>
          </div>

          <div className="tt-metrics">
            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Folder size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Total Projects</div>
                <div className="tt-metric-value">{metrics.totalProjects}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Tasks size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Tasks in Progress</div>
                <div className="tt-metric-value">{metrics.tasksInProgress}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Check size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Completed Tasks</div>
                <div className="tt-metric-value">{metrics.completedTasks}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Users size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Team Members</div>
                <div className="tt-metric-value">{metrics.teamMembers}</div>
              </div>
            </div>
          </div>

          <div className="tt-board-controls">
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: "8px",
                  top: "6px",
                  color: "#5e6c84",
                }}
              >
                <Search size={16} />
              </span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                type="text"
                placeholder="Search board"
                style={{
                  padding: "6px 6px 6px 28px",
                  border: "1px solid #dfe1e6",
                  borderRadius: "3px",
                  width: "220px",
                }}
              />
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <button
                className="tt-filter-btn"
                onClick={() => handleButtonClick("Group")}
              >
                <Filter size={14} /> Group <ChevronDown size={12} />
              </button>

              <div style={{ display: "flex", background: "#ebecf0", padding: "2px", borderRadius: "6px", gap: "2px" }}>
                <button
                  className="btn-icon"
                  style={{
                    background: activeTab === "List" ? "#fff" : "transparent",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    boxShadow: activeTab === "List" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                  }}
                  onClick={() => navigate("/listpage", { state: { projectId: currentProjectId, projectName: currentProjectName } })}
                >
                  <List size={18} />
                </button>
                <button
                  className="btn-icon"
                  style={{
                    background: activeTab === "Board" ? "#fff" : "transparent",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    boxShadow: activeTab === "Board" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                  }}
                  onClick={() => setActiveTab("Board")}
                >
                  <LayoutGrid size={18} />
                </button>
              </div>

              <div style={{ width: "1px", height: "24px", background: "#dfe1e6", margin: "0 4px" }}></div>

              <button
                className="btn-icon"
                onClick={() => exportCSV()}
                title="Export CSV"
              >
                <Download size={18} />
              </button>
              <button
                className="btn-icon"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                title="Import CSV"
              >
                <Upload size={18} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={onImportFile}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {activeTab === "Members" && (
            <div className="tt-members-panel">
              <div className="tt-members-header">
                <h2 className="tt-members-title">
                  <Users size={20} style={{ marginRight: 8, verticalAlign: "middle" }} />
                  Project Members
                  <span className="tt-members-count">{projectMembersData.length}</span>
                </h2>
                <button
                  className="tt-invite-action-btn"
                  onClick={() => setIsInviteModalOpen(true)}
                >
                  <Plus size={15} /> Invite Member
                </button>
              </div>

              {projectMembersData.length === 0 ? (
                <div className="tt-members-empty">
                  <Users size={48} className="tt-members-empty-icon" />
                  <p>No members yet. Invite someone to get started!</p>
                  <button
                    className="tt-invite-action-btn"
                    onClick={() => setIsInviteModalOpen(true)}
                  >
                    <Plus size={15} /> Invite Member
                  </button>
                </div>
              ) : (
                <div className="tt-members-grid">
                  {projectMembersData.map((member, idx) => {
                    const role = member.role || "member";
                    const uid = member.userId || member.user_id || "";
                    const cachedInfo = memberInfoCache[String(uid)];
                    const displayName = cachedInfo?.name || cachedInfo?.email || member.email || member.name || null;
                    const displayEmail = cachedInfo?.email || member.email || null;
                    // Avatar initials: prefer first letter of name/email, fallback to uid digits
                    const initials = displayName
                      ? displayName.slice(0, 2).toUpperCase()
                      : uid ? String(uid).slice(0, 2).toUpperCase() : "?";
                    const roleColor =
                      role === "admin"
                        ? { bg: "rgba(108,84,231,0.12)", color: "#6c5ce7" }
                        : role === "viewer"
                          ? { bg: "rgba(54,179,126,0.12)", color: "#36b37e" }
                          : { bg: "rgba(0,82,204,0.10)", color: "#0052cc" };
                    return (
                      <div key={member.projectMemberId || idx} className="tt-member-card">
                        <div className="tt-member-avatar">{initials}</div>
                        <div className="tt-member-info">
                          <div className="tt-member-name">
                            {displayName || (uid ? `User #${uid}` : `Member ${idx + 1}`)}
                          </div>
                          <div className="tt-member-meta">
                            {displayEmail && displayEmail !== displayName && (
                              <span style={{ marginRight: 6 }}>{displayEmail}</span>
                            )}
                            {member.addedAt ? `Joined ${new Date(member.addedAt).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                        <span
                          className="tt-member-role-badge"
                          style={{ background: roleColor.bg, color: roleColor.color }}
                        >
                          {role}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="tt-board-scroll" style={{ display: activeTab === "Members" ? "none" : undefined }}>
            <div className="tt-board-columns">
              {["todo", "inprogress", "review", "done"].map((colKey) => {
                const colLabel =
                  colKey === "todo"
                    ? "TO DO"
                    : colKey === "inprogress"
                      ? "IN PROGRESS"
                      : colKey === "review"
                        ? "IN REVIEW"
                        : "DONE";
                const colTasks = filterTasks(displayTasks[colKey] || []);
                return (
                  <div
                    className={`tt-column ${colKey === "done" ? "done" : ""}`}
                    key={colKey}
                  >
                    <div className="tt-column-header">
                      {colLabel}{" "}
                      <span className="tt-card-count">
                        {(displayTasks[colKey] || []).length}
                      </span>
                    </div>
                    <div
                      className={`tt-task-list`}
                      onDragEnter={(e) => e.preventDefault()}
                      onDragOver={(e) =>
                        handleDragOver(e, colKey, (displayTasks[colKey] || []).length)
                      }
                      onDrop={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDrop(colKey, (displayTasks[colKey] || []).length);
                      }}
                    >
                      {colTasks.map((task, idx) => (
                        <div
                          key={task.id}
                          className={`tt-task-card priority-${(task.priority || "Medium").toLowerCase().replace(/\s+/g, "-")}`}
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            handleDragStart(e, colKey, idx);
                          }}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            e.stopPropagation();
                            handleDragOver(e, colKey, idx);
                          }}
                          onDragEnter={(e) => {
                            e.preventDefault();
                          }}
                          onDrop={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDrop(colKey, idx);
                          }}
                          onClick={() => handleTaskClick(task.title)}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                className={`tt-priority-badge ${(task.priority || "Medium").toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                {task.priority || "Medium"}
                              </span>
                              <div className="tt-card-title">{task.title}</div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              <Edit
                                size={14}
                                style={{ cursor: "pointer" }}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTask(task.id);
                                }}
                              />
                              <Trash
                                size={14}
                                style={{ cursor: "pointer" }}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task.id);
                                }}
                              />
                            </div>
                          </div>
                          <div className="tt-card-meta">
                            <div className="tt-card-left">
                              <div className="tt-date">
                                <Calendar size={12} /> {task.startDate ? `${task.startDate} - ` : ''}{task.date}
                              </div>
                              <div className="tt-card-id">
                                <Book size={12} /> {task.id}
                              </div>
                              {task.timeSpent > 0 && (
                                <div className="tt-time-spent" title="Time Spent" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#0052CC', fontSize: '11px', fontWeight: 'bold' }}>
                                  <History size={12} /> {task.timeSpent}h
                                </div>
                              )}
                            </div>
                            <div className="tt-user-avatar">
                              {task.assignee &&
                                task.assignee !== "Unassigned" ? (
                                String(task.assignee).charAt(0)
                              ) : (
                                <User size={14} />
                              )}
                              {task.members && task.members > 1 && (
                                <span className="tt-member-count">
                                  {task.members}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div
                        className="create-task-btn"
                        onClick={() =>
                          handleCreateTask(
                            colKey === "todo"
                              ? "To Do"
                              : colKey === "inprogress"
                                ? "In Progress"
                                : colKey === "review"
                                  ? "In Review"
                                  : "Done",
                          )
                        }
                      >
                        + Create
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>

        {
          isCreateModalOpen && (
            <CreateTask
              onClose={() => {
                setIsCreateModalOpen(false);
                setEditingTask(null);
              }}
              onAddTask={handleAddTaskToBoard}
              initialColumn={defaultColumn}
              initialData={editingTask?.data}
              currentProjectId={currentProjectId}
            />
          )
        }

        {showTimeSpentPopup && (
          <div className="create-task-overlay">
            <div className="create-task-modal" style={{ maxWidth: '400px', height: 'auto' }}>
              <div className="create-task-header">
                <h2>Log Time Spent</h2>
                <div className="header-actions">
                  <button className="icon-btn" onClick={() => { setShowTimeSpentPopup(false); setPendingTaskMovement(null); }}><ChevronRight size={18} /></button>
                </div>
              </div>
              <div className="create-task-content" style={{ padding: '20px' }}>
                <div className="form-group">
                  <label className="form-label">How many hours did you spend on this task?</label>
                  <div className="input-with-unit">
                    <input
                      type="number"
                      className="form-input"
                      value={timeSpentInput}
                      onChange={(e) => setTimeSpentInput(e.target.value)}
                      autoFocus
                    />
                    <span className="input-unit">h</span>
                  </div>
                </div>
              </div>
              <div className="create-task-footer">
                <button className="btn btn-link" onClick={() => { setShowTimeSpentPopup(false); setPendingTaskMovement(null); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleTimeSpentSubmit}>Complete Task</button>
              </div>
            </div>
          </div>
        )}

        {/* Invite People Modal */}
        {isInviteModalOpen && (
          <div className="create-task-overlay" onClick={() => setIsInviteModalOpen(false)}>
            <div
              className="create-task-modal"
              style={{ maxWidth: '480px', height: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="create-task-header">
                <h2><Users size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />Invite People</h2>
                <div className="header-actions">
                  <button className="icon-btn" onClick={() => setIsInviteModalOpen(false)}>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
              <div className="create-task-content" style={{ padding: '24px' }}>
                <p style={{ margin: '0 0 20px', color: '#5e6c84', fontSize: '14px' }}>
                  Invite a team member to <strong>{currentProjectName || 'this project'}</strong>.
                  They must already have an account.
                </p>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="e.g. teammate@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleInvitePeople()}
                    autoFocus
                    style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
                    Role
                  </label>
                  <select
                    className="form-input"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box' }}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="create-task-footer">
                <button
                  className="btn btn-link"
                  onClick={() => { setIsInviteModalOpen(false); setInviteEmail(''); }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleInvitePeople}
                  disabled={isInviting || !inviteEmail.trim()}
                >
                  {isInviting ? 'Inviting...' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div >
    </div >
  );
};

export default TaskTools;
